/**
 * DevExpress "missing sheet" references → plain sheet references.
 *
 * A template author can write a formula against a sheet that is NOT in the template but appears at
 * run time — PFAS_533_Waters (8114) G2:H4 `AVERAGEIFS(RawDataSheet!T:T, …)` reads the sheet OG's
 * import adds (SpreadsheetEntryController :24957 / :11978), VOC (8086) BA8 `INDEX('CCV1'!J:J, …)`
 * reads the per-run "CCV1" sheet the Model-1 merge creates. DevExpress can only SAVE such a formula
 * to xlsx as an EXTERNAL reference: `[3]RawDataSheet!T:T` plus an `externalLink` part whose
 * relationship type is `…/xlExternalLinkPath/xlPathMissing` and whose target is the sheet name.
 * On LOAD it reads it back as the plain `RawDataSheet!T:T` (verified in DevExpress 20.1: the loaded
 * template's G2 formula text has no `[3]`), so once the sheet exists the formula resolves — that is
 * what the OG blobs hold.
 *
 * ExcelJS keeps the `[3]` text and DROPS the externalLink parts on write, so G6 rendered the cell
 * blank (Univer cannot resolve an external book) and every band copy of `'[3]CCV1'!` was even
 * row-shifted to `'[3]CCV61'!`. This rewrites each path-missing `[n]Name!` to `Name!` (quoted when
 * the name needs it) in the worksheet formulas BEFORE ExcelJS loads the template — the DevExpress
 * load semantics, derived from the template's own link parts, never a per-template list.
 *
 * A reference to a genuinely external workbook (any other link type) is left untouched.
 */

import { sheetRef } from './sdmsSummaryOrientation';

const PATH_MISSING = /\/xlPathMissing$/i;

/** Decode the XML entities a formula body can carry. */
function xmlDecode(s: string): string {
  return s.replace(/&apos;/g, "'").replace(/&quot;/g, '"').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');
}
function xmlEncode(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Rewrite `[n]Name!` / `'[n]Name'!` in ONE formula (no leading "=") for the link indexes in
 *  `links` (1-based index → sheet name). Text inside double-quoted literals is never touched.
 *  Exported for unit testing. */
export function resolveFormulaSheetRefs(formula: string, links: ReadonlyMap<number, string>): string {
  if (!links.size || !formula.includes('[')) return formula;
  return formula.split(/("(?:[^"]|"")*")/).map((part, i) => i % 2 === 1 ? part
    : part
      // quoted form: '[3]CCV1'!  (the sheet name may itself hold '' escapes)
      .replace(/'\[(\d+)\]((?:[^']|'')+)'!/g, (m, n: string, name: string) =>
        links.has(Number(n)) ? `${sheetRef(name.replace(/''/g, "'"))}!` : m)
      // bare form: [3]RawDataSheet!
      .replace(/\[(\d+)\]([A-Za-z_][A-Za-z0-9_.]*)!/g, (m, n: string, name: string) =>
        links.has(Number(n)) ? `${sheetRef(name)}!` : m),
  ).join('');
}

/** The template's path-missing external links, by their 1-based formula index (`[n]`), read from
 *  `xl/workbook.xml` <externalReferences> order → workbook rels → each externalLink part + its rels. */
async function pathMissingLinks(zip: import('jszip')): Promise<Map<number, string>> {
  const out = new Map<number, string>();
  const wbXml = await zip.file('xl/workbook.xml')?.async('string');
  const wbRels = await zip.file('xl/_rels/workbook.xml.rels')?.async('string');
  if (!wbXml || !wbRels) return out;
  const block = /<externalReferences>([\s\S]*?)<\/externalReferences>/.exec(wbXml)?.[1];
  if (!block) return out;
  const ids = [...block.matchAll(/<externalReference\b[^>]*r:id="([^"]+)"/g)].map(m => m[1]!);
  for (let i = 0; i < ids.length; i++) {
    const rel = new RegExp(`<Relationship\\b[^>]*Id="${ids[i]}"[^>]*>`).exec(wbRels)?.[0];
    const target = rel && /Target="([^"]+)"/.exec(rel)?.[1];
    if (!target) continue;
    const part = `xl/${target.replace(/^\/?xl\//, '').replace(/^\//, '')}`;
    const linkXml = await zip.file(part)?.async('string');
    const relsPath = part.replace(/([^/]+)$/, '_rels/$1.rels');
    const linkRels = await zip.file(relsPath)?.async('string');
    if (!linkXml || !linkRels) continue;
    const linkRel = /<Relationship\b[^>]*>/.exec(linkRels)?.[0] ?? '';
    const type = /Type="([^"]+)"/.exec(linkRel)?.[1] ?? '';
    if (!PATH_MISSING.test(type)) continue;
    const name = /<sheetName\s+val="([^"]+)"/.exec(linkXml)?.[1] ?? /Target="([^"]+)"/.exec(linkRel)?.[1];
    if (name) out.set(i + 1, xmlDecode(name));
  }
  return out;
}

/**
 * Rewrite every path-missing external sheet reference in the template's worksheet formulas to the
 * plain sheet reference DevExpress reads it as. Returns `templateBuf` unchanged when the template has
 * no such link or no formula uses one (the common case — one zip read, no re-zip).
 */
export async function resolveMissingSheetRefs(templateBuf: ArrayBuffer): Promise<ArrayBuffer> {
  try {
    const JSZip = (await import('jszip')).default;
    const zip = await JSZip.loadAsync(templateBuf);
    const links = await pathMissingLinks(zip);
    if (!links.size) return templateBuf;
    let changed = false;
    for (const path of Object.keys(zip.files)) {
      if (!/^xl\/worksheets\/sheet\d+\.xml$/.test(path)) continue;
      const xml = await zip.file(path)!.async('string');
      if (!/\[\d+\]/.test(xml)) continue;
      const next = xml.replace(/(<f\b[^>]*>)([^<]*)(<\/f>)/g, (m, open: string, body: string, close: string) => {
        const f = xmlDecode(body);
        const r = resolveFormulaSheetRefs(f, links);
        return r === f ? m : `${open}${xmlEncode(r)}${close}`;
      });
      if (next !== xml) { zip.file(path, next); changed = true; }
    }
    if (!changed) return templateBuf;
    return await zip.generateAsync({ type: 'arraybuffer' });
  } catch (e) {
    console.warn('[SDMS] missing-sheet reference resolve skipped', e);
    return templateBuf;
  }
}
